import { Button, Card, CardContent, CardHeader, CardTitle, EmptyState, SkeletonRows } from '@bia-auction/ui';
import { t } from '@bia-auction/shared-i18n';
import type { PublicMembershipTier } from '@bia-auction/shared-schema';
import { publicMembershipTierSchema } from '@bia-auction/shared-schema';
import { useQuery } from '@tanstack/react-query';
import { BadgeCheck, Info } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import { PaymentDialog } from '../components/PaymentDialog';
import { ErrorMsg } from '../components/Feedback';
import { PageHero } from '../components/PageHero';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { formatKurus } from '../lib/format';
import { PHOTOS } from '../lib/photos';
import { isMember } from '../lib/roles';

export function MembershipPage() {
  const { state } = useAuth();
  const alreadyMember = isMember(state.roles);
  const [buying, setBuying] = useState<PublicMembershipTier | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['memberships'],
    queryFn: () => api<PublicMembershipTier[]>('/memberships', {}, z.array(publicMembershipTierSchema)),
  });

  // Keep the hero through loading, failure and emptiness — the page has an identity before it
  // has a catalog, and a lone skeleton doesn't say which page you are on.
  if (isLoading || error || !data || data.length === 0)
    return (
      <div className="space-y-6">
        <PageHero photo={PHOTOS.fleetRows} title={t('bidder.membership.title')} />
        {isLoading ? (
          <SkeletonRows rows={3} />
        ) : error ? (
          <ErrorMsg error={error} />
        ) : (
          <EmptyState icon={BadgeCheck} title={t('bidder.membership.empty')} />
        )}
      </div>
    );

  return (
    <div className="space-y-6">
      <PageHero
        photo={PHOTOS.fleetRows}
        title={t('bidder.membership.title')}
        subline={t('bidder.membership.subline', { n: data.length })}
      />
      {alreadyMember && (
        <p className="flex items-center gap-2 rounded-md bg-info-tint px-3 py-2 text-sm text-info-tint-fg">
          <Info aria-hidden className="h-4 w-4 shrink-0" />
          {t('bidder.membership.alreadyMember')}
        </p>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {data.map((tier) => (
          <Card key={tier.id}>
            <CardHeader>
              <CardTitle>{tier.name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="font-head text-h2 font-semibold tabular-nums">
                {formatKurus(tier.amount)}
              </div>
              {/* One filled Satın al per tier card (the admin Config per-card pattern). */}
              {/* Buying the tier is the page's purpose — signature CTA, one per card row. */}
              <Button
                variant="brand"
                className="h-11 w-full sm:h-9"
                disabled={alreadyMember}
                onClick={() => setBuying(tier)}
              >
                {t('bidder.membership.buy')}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <PaymentDialog
        open={buying !== null}
        onOpenChange={(o) => !o && setBuying(null)}
        title={buying?.name ?? t('bidder.membership.title')}
        path="/memberships/purchase"
        extraBody={buying ? { membershipId: buying.id } : undefined}
      />
    </div>
  );
}
